// libraries
import React, { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { makeStyles } from '@mui/styles'
import { useGridApiRef } from '@mui/x-data-grid-pro'
import { Typography, Stack, IconButton, TableRow, TableCell, Tooltip } from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import { useTheme } from '@mui/material/styles'
// queries
import { useVoidPaymentMutation } from 'services/payments'

// components
import BasicDataGrid from 'components/generic/datagrid'
import AlertModal from 'components/generic/modals/dialog-modal/AlertModal'

// helpers and utils
import { PAYMENT_METHODS_VALUES, ENTITY_IN_LOCK_PERIOD_MESSAGE } from 'utils/constants'
import { formatNumber, dispatchSnackbar } from 'utils/helpers'
import { dateFormatter } from 'utils/data-manipulation/valueFormatter'
import { capAllWords } from 'utils/data-manipulation/common'
const useStyles = makeStyles(theme => ({
  root: {
    border: 'none',
    // remove header padding to align headers with column values
    '& .MuiDataGrid-columnHeaderTitleContainer': {
      padding: theme.spacing(0),
    },
    '& .MuiDataGrid-columnHeader': {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
    },
    '& .MuiDataGrid-cell': {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
    },
    '& .MuiDataGrid-columnSeparator': {
      visibility: 'hidden',
    },
    // replace default hover / active-row styling from datagrid
    '& .MuiDataGrid-row': {
      '&.Mui-selected': {
        backgroundColor: 'inherit',
        '&:hover': {
          backgroundColor: 'rgba(0, 0, 0, 0.08)',
        },
      },
      '&:not(.Mui-selected)': {
        backgroundColor: 'inherit',
        '&:hover': {
          backgroundColor: 'rgba(0, 0, 0, 0.08)',
        },
      },
    },
    '& .MuiDataGrid-columnHeader:focus': {
      outline: 'none',
    },
    '& .MuiDataGrid-cell:focus': {
      outline: 'none',
    },
    '& .MuiDataGrid-cell:focus-within': {
      outline: 'none',
    },
  },
}))

const BillPaymentsTable = ({
  billId,
  paymentIds,
  paymentsData,
  loadingPaymentsData,
  setLoadingPaymentsData,
  fetchPaymentsData,
  pricePrepend,
  disabled,
}) => {
  const dispatch = useDispatch()
  const classes = useStyles()
  const apiRef = useGridApiRef()
  const theme = useTheme()
  const { settings } = useSelector(state => state.settings)
  const { lockAccountingPeriod } = settings?.overviewSettings?.overview || {}

  const { enableLockAccountingV2 } = useSelector(state => state.featureFlags?.variables || {})

  const [showVoidModal, setShowVoidModal] = useState(false)
  const [selectedPaymentInfo, setSelectedPaymentInfo] = useState({})

  const [voidPayment, { isLoading: isVoidingPayment }] = useVoidPaymentMutation()

  const onVoidPayment = async () => {
    await voidPayment({ billId, paymentId: selectedPaymentInfo.id })
      .unwrap()
      .then(() => {
        dispatchSnackbar(dispatch, 'success', 'Payment Voided')
        setShowVoidModal(false)
        setSelectedPaymentInfo({})
        setLoadingPaymentsData(true)
        fetchPaymentsData(paymentIds)
      })
      .catch(error => {
        const errorMessage =
          error?.data?.error || error?.error || error?.message || 'An error occurred voiding the payment.'
        dispatchSnackbar(dispatch, 'error', errorMessage)
      })
  }

  const paymentColumns = useMemo(() => {
    // Check if any payment has gsPayPayment
    const hasGsPayPayment = paymentsData.some(payment => payment.gsPayPayment)
    const hasCheckPayment = paymentsData.some(payment => payment.checkNumber)

    const baseColumns = [
      {
        field: 'paymentDate',
        headerName: 'Payment Date',
        flex: 1,
        headerAlign: 'left',
        align: 'left',
        valueFormatter: params => {
          const { value } = params
          return dateFormatter(new Date(value))
        },
        sortable: false,
      },
      {
        field: 'paymentMethod',
        headerName: 'Payment Method',
        flex: 1,
        headerAlign: 'left',
        align: 'left',
        valueFormatter: params => {
          const { value } = params
          return PAYMENT_METHODS_VALUES[value?.toUpperCase()] || ''
        },
        sortable: false,
      },
    ]

    // Conditionally add gsPayPayment column
    if (hasGsPayPayment) {
      baseColumns.push({
        field: 'gsPayPayment',
        headerName: 'Payment Status',
        flex: 1,
        headerAlign: 'left',
        align: 'left',
        sortable: false,
        valueGetter: params => {
          return params.row.gsPayPayment?.status ? capAllWords(params.row.gsPayPayment?.status.toLowerCase()) : ''
        },
      })
    }

    if (hasCheckPayment) {
      baseColumns.push({
        field: 'checkNumber',
        headerName: 'Check #',
        flex: 1,
        headerAlign: 'left',
        align: 'left',
        sortable: false,
      })
    }

    baseColumns.push(
      {
        field: 'total',
        headerName: 'Payment Amount',
        flex: 1,
        headerAlign: 'right',
        align: 'right',
        valueGetter: params => {
          const { total = 0 } = params.row
          return `${pricePrepend}${formatNumber(total.toFixed(2))}`
        },
        sortable: false,
      },
      {
        field: 'actions',
        headerName: '',
        renderCell: params => {
          const { row } = params
          const { paymentDate } = row || {}

          let isInLockedAccountingPeriodV1

          if (enableLockAccountingV2) {
            isInLockedAccountingPeriodV1 = false // if lock accounting V2 is turned on, we do not need to disable any fields
          } else if (!lockAccountingPeriod || !lockAccountingPeriod.isLockedForAccounting) {
            isInLockedAccountingPeriodV1 = false
          } else {
            isInLockedAccountingPeriodV1 =
              new Date(paymentDate).getTime() < new Date(lockAccountingPeriod.lockAccountingDate).getTime()
          }

          return row.id === 'total' ? null : (
            <Tooltip title={isInLockedAccountingPeriodV1 ? ENTITY_IN_LOCK_PERIOD_MESSAGE : ''}>
              <div>
                <IconButton
                  color="error"
                  size="small"
                  sx={{
                    fill: 'red',
                  }}
                  aria-label="delete"
                  onClick={() => {
                    setSelectedPaymentInfo(row)
                    setShowVoidModal(true)
                  }}
                  disabled={disabled || isInLockedAccountingPeriodV1 || row.gsPayPayment?.status}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </div>
            </Tooltip>
          )
        },
        headerAlign: 'center',
        align: 'center',
        sortable: false,
      },
    )

    return baseColumns
  }, [paymentsData, pricePrepend, disabled, lockAccountingPeriod, enableLockAccountingV2])

  return (
    <>
      <AlertModal
        open={showVoidModal}
        modalType="delete"
        title="Void This Payment"
        question="Are you sure you want to void this payment?"
        description={
          <>
            <TableRow>
              <TableCell sx={{ pl: 1, pt: 0, pb: 0, fontWeight: 700, borderBottom: 'none' }}>Payment Date:</TableCell>
              <TableCell sx={{ pl: 1, pt: 0, pb: 0, borderBottom: 'none' }}>
                {dateFormatter(new Date(selectedPaymentInfo?.paymentDate))}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ pl: 1, pt: 0, pb: 0, fontWeight: 700, borderBottom: 'none' }}>Payment Method:</TableCell>
              <TableCell sx={{ pl: 1, pt: 0, pb: 0, borderBottom: 'none' }}>
                {PAYMENT_METHODS_VALUES[selectedPaymentInfo?.paymentMethod?.toUpperCase()] || ''}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ pl: 1, pt: 0, pb: 0, fontWeight: 700, borderBottom: 'none' }}>Check Number:</TableCell>
              <TableCell sx={{ pl: 1, pt: 0, pb: 0, borderBottom: 'none' }}>
                {selectedPaymentInfo?.checkNumber || ''}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ pl: 1, pt: 0, pb: 0, fontWeight: 700, borderBottom: 'none' }}>Payment Amount:</TableCell>
              <TableCell sx={{ pl: 1, pt: 0, pb: 0, borderBottom: 'none' }}>
                {pricePrepend}
                {formatNumber((selectedPaymentInfo?.total || 0).toFixed(2))}
              </TableCell>
            </TableRow>
          </>
        }
        confirmLabel="YES, CONFIRM VOID"
        cancelLabel="GO BACK"
        onClose={() => {
          setShowVoidModal(false)
          setSelectedPaymentInfo({})
        }}
        onSubmit={onVoidPayment}
      />
      <Stack p={3}>
        <Typography variant="h6" gutterBottom color="textSecondary">
          Payments Made
        </Typography>
        <BasicDataGrid
          className={classes.root}
          density="compact"
          height="auto"
          apiRef={apiRef}
          loading={loadingPaymentsData || isVoidingPayment}
          rows={paymentsData}
          columns={paymentColumns}
          disableColumnMenu
          hideFooter
        />
        {paymentsData.some(payment => payment.gsPayPayment?.status === 'INITIATED') && (
          <Typography
            variant="caption"
            color="text.secondary"
            whiteSpace="pre-wrap"
            sx={{ color: theme.palette.info.main }}
          >
            Contact GreenSpark Support to cancel before 3PM EST
          </Typography>
        )}
      </Stack>
    </>
  )
}

BillPaymentsTable.propTypes = {
  billId: PropTypes.string.isRequired,
  paymentIds: PropTypes.array.isRequired,
  paymentsData: PropTypes.array,
  loadingPaymentsData: PropTypes.bool,
  setLoadingPaymentsData: PropTypes.func,
  fetchPaymentsData: PropTypes.func.isRequired,
  pricePrepend: PropTypes.string,
  disabled: PropTypes.bool,
}

BillPaymentsTable.defaultProps = {
  paymentsData: [],
  loadingPaymentsData: false,
  setLoadingPaymentsData: () => {},
  pricePrepend: '$',
  disabled: false,
}

export default BillPaymentsTable
